import { useEffect, useState, Suspense, useRef } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { Canvas, useThree, useFrame } from '@react-three/fiber'
import { Stage, useGLTF, useProgress, PerspectiveCamera, CameraControls } from '@react-three/drei'
import { getExperience } from '../api/experience'
import { LoadingScreen } from '../components/Loading'
import { getAssetUrl } from '../utils'
import type ExperienceData from '../types/experience'

const Model = ({ url, onClick, spin }: { url: string; onClick?: () => void; spin?: boolean }) => {
  const { scene } = useGLTF(url)

  const [initialRotation, setInitialRotation] = useState(0)
  const wasSpinning = useRef<boolean>(false)

  useEffect(() => {
    if (scene) {
      setInitialRotation(scene.rotation.y)
    }
  }, [scene])

  useFrame(() => {
    if (spin) {
      scene.rotation.y += 0.001
      wasSpinning.current = true
    } else {
      if (wasSpinning.current) {
        scene.rotation.y = initialRotation
        wasSpinning.current = false
      }
    }
  })

  return <primitive object={scene} onClick={onClick} />
}

// Camera debug component to show current position and target
const CameraDebug = ({
  onCameraUpdate,
  controlsRef,
}: {
  onCameraUpdate: (info: {
    position: { x: number; y: number; z: number }
    target: { x: number; y: number; z: number }
    distance: number
    zoom: number
    azimuth: number
    polar: number
  }) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  controlsRef: React.RefObject<any>
}) => {
  const { camera } = useThree()

  useFrame(() => {
    if (camera) {
      // Get camera position
      const position = {
        x: Number(camera.position.x.toFixed(2)),
        y: Number(camera.position.y.toFixed(2)),
        z: Number(camera.position.z.toFixed(2)),
      }

      // Get target from controls ref
      let target = { x: 0, y: 0, z: 0 }
      if (controlsRef.current && controlsRef.current.target) {
        const controlsTarget = controlsRef.current.target
        target = {
          x: Number(controlsTarget.x.toFixed(2)),
          y: Number(controlsTarget.y.toFixed(2)),
          z: Number(controlsTarget.z.toFixed(2)),
        }
      }

      // Calculate distance from camera to target
      const distance = Math.sqrt(
        Math.pow(camera.position.x - target.x, 2) +
          Math.pow(camera.position.y - target.y, 2) +
          Math.pow(camera.position.z - target.z, 2)
      )

      // Get azimuth and polar from OrbitControls
      const azimuth = controlsRef.current?.getAzimuthalAngle() || 0
      const polar = controlsRef.current?.getPolarAngle() || 0

      // Get actual camera zoom (for orthographic cameras) or use 1 for perspective
      const zoom =
        camera.type === 'OrthographicCamera'
          ? Number((camera as { zoom: number }).zoom.toFixed(2))
          : 1

      const info = {
        position,
        target,
        distance: Number(distance.toFixed(2)),
        zoom,
        azimuth: Number(azimuth.toFixed(3)),
        polar: Number(polar.toFixed(3)),
      }
      onCameraUpdate(info)
    }
  })

  return null // This component doesn't render anything in the 3D scene
}

type CameraMode = 'driver' | 'engineer'

export default function Experience() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const [experience, setExperience] = useState<ExperienceData | null>(null)
  // Get mode from URL params, default to 'engineer'
  const mode = (searchParams.get('mode') as CameraMode) || 'engineer'

  const [progressBar, setProgressBar] = useState(0)
  const [modelUrl, setModelUrl] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  const [currentCameraInfo, setCurrentCameraInfo] = useState({
    position: { x: 0, y: 0, z: 0 },
    target: { x: 0, y: 0, z: 0 },
    distance: 0,
    zoom: 1,
    azimuth: 0,
    polar: 0,
  })
  // Get camera settings from experience data
  const [cameraPosition, setCameraPosition] = useState<[number, number, number]>([0, 0, 5])
  const [cameraFov, setCameraFov] = useState(50)
  const [cameraNear, setCameraNear] = useState(0.1)
  const [cameraFar, setCameraFar] = useState(1000)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const controlsRef = useRef<any>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cameraRef = useRef<any>(null)

  const { progress } = useProgress()

  useEffect(() => {
    if (progress > 0) {
      setProgressBar(25 + progress * 0.75)
    }
  }, [progress])

  useEffect(() => {
    const loadExperience = async () => {
      if (!id) return

      try {
        setProgressBar(10)
        const data = await getExperience(id)
        setExperience(data)
        setProgressBar(20)

        const modelName = data.model.source.filename
        if (modelName) {
          const fullUrl = getAssetUrl(modelName)
          setModelUrl(fullUrl)
          setProgressBar(25)
        } else {
          setError('Failed to load experience')
        }

        if (data.model && data.model.defaultCamera) {
          setCameraFov(data.model.defaultCamera.fov ?? 50)
          setCameraNear(data.model.defaultCamera.near ?? 0.1)
          setCameraFar(data.model.defaultCamera.far ?? 1000)
          // CameraControls doesn't need defaultTarget configuration
          setCameraPosition([
            data.model.defaultCamera.x ?? 0,
            data.model.defaultCamera.y ?? 0,
            data.model.defaultCamera.z ?? 5,
          ])
        }
      } catch (error) {
        console.error('Error loading experience:', error)
      }
    }

    loadExperience()
  }, [id, mode])

  if (error && !modelUrl) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-black text-xl text-white">
        {error}
      </div>
    )
  }

  if (!experience) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-black text-xl text-white">
        Experience not found
      </div>
    )
  }

  if (progressBar < 25 || modelUrl === '') {
    return <LoadingScreen progress={progressBar} />
  }

  return (
    <div className="relative h-screen w-screen overflow-hidden">
      <Suspense fallback={<LoadingScreen progress={progressBar} />}>
        {/* 2D Background */}
        <div className="dotted-bg absolute top-0 left-0 z-0 h-full w-full"></div>

        {/* 3D Experience */}
        <Canvas
          camera={{
            position: [0, 0, 5],
            fov: 50,
            near: 0.1,
            far: 1000,
          }}
          className="!absolute top-0 left-0 z-10 h-full w-full"
          gl={{ alpha: true, antialias: true }}
        >
          <Stage adjustCamera={false}>
            <Model url={modelUrl} spin={false} />
          </Stage>

          <ambientLight intensity={0.5} />
          <directionalLight position={[5, 5, 5]} />
          <PerspectiveCamera
            ref={cameraRef}
            makeDefault
            fov={cameraFov}
            near={cameraNear}
            far={cameraFar}
            position={cameraPosition}
          />
          <CameraControls
            ref={controlsRef}
            camera={cameraRef.current}
            dollySpeed={0.5}
            truckSpeed={2}
            minDistance={controlsRef.current ? controlsRef.current.distance : Number.EPSILON}
            maxDistance={controlsRef.current ? controlsRef.current.distance : Infinity}
          />

          {/* Camera debug component */}
          <CameraDebug
            controlsRef={controlsRef}
            onCameraUpdate={info => {
              setCurrentCameraInfo(info)
            }}
          />
        </Canvas>

        {/* Camera Settings Helper */}
        <div className="bg-opacity-50 absolute bottom-0 left-0 z-10 rounded-tr-lg bg-black p-4 text-white select-none">
          <h3 className="mb-2 font-bold">Camera Settings</h3>
          <div className="space-y-4 text-sm">
            <div>
              <p className="mb-2 text-xs text-yellow-300">📋 For defaultCamera settings, use:</p>
              <div className="rounded bg-gray-800 p-2 font-mono text-xs">
                <div>x: {currentCameraInfo.position.x}</div>
                <div>y: {currentCameraInfo.position.y}</div>
                <div>z: {currentCameraInfo.position.z}</div>
              </div>
            </div>
            <div>
              <p className="mb-2 text-xs text-yellow-300">🎯 And set target to:</p>
              <div className="rounded bg-gray-800 p-2 font-mono text-xs">
                <div>
                  targetX: {currentCameraInfo.target.x}
                  <br />
                  targetY: {currentCameraInfo.target.y}
                  <br />
                  targetZ: {currentCameraInfo.target.z}
                </div>
              </div>
            </div>
            <div>
              <p className="mb-2 text-xs text-yellow-300">🔍 And set azimuth and polar to:</p>
              <div className="rounded bg-gray-800 p-2 font-mono text-xs">
                <div>azimuth: {currentCameraInfo.azimuth}</div>
                <div>polar: {currentCameraInfo.polar}</div>
              </div>
            </div>
          </div>
        </div>
      </Suspense>
    </div>
  )
}
